import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import {
    AlertTriangle,
    ArrowLeft,
    Check,
    ChevronDown,
    ChevronUp,
    Info,
    Minus,
    Plus,
    ShoppingCart,
    UtensilsCrossed,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FeatureGate } from '@/components/features/FeatureGate';
import { ShoppingListsLockedPage } from '@/components/features/FeatureLockedPage';
import { Feature } from '@/types/auth';
import { cn } from '@/lib/utils';
import { fromLocalDateStr } from '@/lib/date';
import {
    useGeneratePlanShoppingList,
    useMyPlan,
    type MyPlan,
    type PlanDay,
    type PlanMeal,
} from '@/services/planShopping';
import {
    useShoppingListNames,
    useShoppingListSummaries,
    type ShoppingListSummary,
} from '@/services/shoppingLists';
import {
    COOKING_FOR_OPTIONS,
    SERVINGS_MAX,
    SERVINGS_MIN,
    buildPayload,
    datesBetween,
    defaultRange,
    defaultSelection,
    describeDays,
    describeResult,
    describeServings,
    formatShortDate,
    isShoppable,
    quickRanges,
    selectedMeals,
    rangeAfterList,
    routeStateFor,
    servingsFor,
    shoppingListPath,
    stepServings,
    uniqueListName,
    type DayRange,
} from '@/lib/planShopping';

const MEAL_TYPE_LABELS: Record<string, string> = {
    BREAKFAST: 'Breakfast',
    LUNCH: 'Lunch',
    DINNER: 'Dinner',
    SNACK: 'Snack',
};

/** Days shown before "Show days 15 to N", unless the chosen range runs past it. */
const INITIAL_DAYS = 14;

// GET /shopping-lists returns each list's stored range as well; the summary
// type in services/shoppingLists.ts predates it.
type ListWithRange = ShoppingListSummary & { startDate?: string | null; endDate?: string | null };

function TickBox({
    checked,
    onCheckedChange,
    disabled,
    label,
}: {
    checked: boolean | 'indeterminate';
    onCheckedChange?: () => void;
    disabled?: boolean;
    label: string;
}) {
    return (
        <CheckboxPrimitive.Root
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-label={label}
            className={cn(
                'peer h-5 w-5 shrink-0 rounded border-2 border-primary bg-background flex items-center justify-center',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
                'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
                'disabled:cursor-not-allowed disabled:border-border disabled:bg-muted',
            )}
        >
            <CheckboxPrimitive.Indicator>
                {checked === 'indeterminate' ? (
                    <Minus className="h-3.5 w-3.5" strokeWidth={3} />
                ) : (
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                )}
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    );
}

function ServingsStepper({
    meal,
    value,
    onStep,
}: {
    meal: PlanMeal;
    value: number;
    onStep: (direction: 1 | -1) => void;
}) {
    const label = `${MEAL_TYPE_LABELS[meal.mealType] ?? meal.mealType}: ${meal.name}`;
    const recipeMakes = meal.recipe?.servings;
    const stepButton =
        'flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-foreground hover:bg-brand-header-wash disabled:cursor-not-allowed disabled:opacity-40';
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <div
                className="flex items-center gap-1"
                role="group"
                aria-label={`Servings for ${label}`}
            >
                <button
                    type="button"
                    className={stepButton}
                    onClick={() => onStep(-1)}
                    disabled={value <= SERVINGS_MIN}
                    aria-label={`Fewer servings of ${label}`}
                >
                    <Minus className="h-4 w-4" />
                </button>
                <span
                    className="min-w-[6.5rem] text-center text-sm font-semibold text-foreground"
                    aria-live="polite"
                    data-testid={`servings-${meal.key}`}
                >
                    {describeServings(value)}
                </span>
                <button
                    type="button"
                    className={stepButton}
                    onClick={() => onStep(1)}
                    disabled={value >= SERVINGS_MAX}
                    aria-label={`More servings of ${label}`}
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
            {recipeMakes ? (
                <span className="text-xs text-muted-foreground">Recipe makes {recipeMakes}</span>
            ) : null}
        </div>
    );
}

function DayRow({
    day,
    selected,
    expanded,
    onToggleExpanded,
    onToggleDay,
    onToggleMeal,
    servingsOf,
    onStepServings,
    onListName,
}: {
    day: PlanDay;
    selected: Set<string>;
    expanded: boolean;
    onToggleExpanded: () => void;
    onToggleDay: () => void;
    onToggleMeal: (key: string) => void;
    servingsOf: (meal: PlanMeal) => number;
    onStepServings: (meal: PlanMeal, direction: 1 | -1) => void;
    onListName?: string;
}) {
    const shoppable = day.meals.filter(isShoppable);
    const tickedCount = shoppable.filter((m) => selected.has(m.key)).length;
    const dayState: boolean | 'indeterminate' =
        tickedCount === 0 ? false : tickedCount === shoppable.length ? true : 'indeterminate';
    const countText =
        shoppable.length === 0
            ? 'Nothing to buy'
            : tickedCount > 0
              ? `${tickedCount} of ${shoppable.length} meal${shoppable.length === 1 ? '' : 's'}`
              : `${shoppable.length} meal${shoppable.length === 1 ? '' : 's'}`;

    return (
        <div
            className={cn(
                'bg-card rounded-xl border shadow-sm',
                expanded ? 'border-brand-header/40' : 'border-transparent',
            )}
            data-testid={`day-${day.dayNumber}`}
        >
            <div className="flex items-center gap-3 px-4 py-3">
                <TickBox
                    checked={dayState}
                    onCheckedChange={onToggleDay}
                    disabled={shoppable.length === 0}
                    label={`Day ${day.dayNumber}`}
                />
                <button
                    type="button"
                    onClick={onToggleExpanded}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} meals for Day ${day.dayNumber}`}
                    className="flex flex-1 min-w-0 items-center gap-2 text-left"
                >
                    <span className="flex-1 min-w-0">
                        <span className="flex flex-wrap items-baseline gap-x-1.5">
                            <span className="font-semibold text-foreground">
                                Day {day.dayNumber}
                            </span>
                            <span className="text-muted-foreground">
                                · {formatShortDate(day.date)}
                            </span>
                        </span>
                        {onListName && (
                            <span className="mt-1 inline-flex max-w-full items-center rounded-md bg-brand-fill-yellow px-1.5 py-0.5 text-xs font-semibold text-brand-fill-ink">
                                <span className="truncate">On {onListName}</span>
                            </span>
                        )}
                    </span>
                    <span className="shrink-0 text-sm text-muted-foreground">{countText}</span>
                    {expanded ? (
                        <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                </button>
            </div>

            {expanded && (
                <ul className="border-t border-border px-4 py-2">
                    {day.meals.length === 0 && (
                        <li className="py-2 text-sm text-muted-foreground">
                            No meals on this day.
                        </li>
                    )}
                    {day.meals.map((meal) => {
                        const canShop = isShoppable(meal);
                        const ticked = canShop && selected.has(meal.key);
                        return (
                            <li key={meal.key} className="flex items-start gap-3 py-2">
                                <div className="pt-4">
                                    <TickBox
                                        checked={ticked}
                                        onCheckedChange={() => onToggleMeal(meal.key)}
                                        disabled={!canShop}
                                        label={`${MEAL_TYPE_LABELS[meal.mealType] ?? meal.mealType}: ${meal.name}`}
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-brand-text-green">
                                        {MEAL_TYPE_LABELS[meal.mealType] ?? meal.mealType}
                                    </div>
                                    <div
                                        className={cn(
                                            'text-sm',
                                            !canShop
                                                ? 'text-muted-foreground'
                                                : ticked
                                                  ? 'text-foreground'
                                                  : 'text-muted-foreground line-through',
                                        )}
                                    >
                                        {meal.name}
                                    </div>
                                    {!canShop && (
                                        <div className="text-xs text-muted-foreground">
                                            No recipe, so nothing to buy
                                        </div>
                                    )}
                                    {canShop && !ticked && (
                                        <div className="text-xs text-muted-foreground">
                                            Left off this list
                                        </div>
                                    )}
                                    {ticked && (
                                        <ServingsStepper
                                            meal={meal}
                                            value={servingsOf(meal)}
                                            onStep={(direction) => onStepServings(meal, direction)}
                                        />
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function Picker({
    plan,
    lists,
    takenNames,
    preselectedListId,
}: {
    plan: MyPlan;
    lists: ListWithRange[];
    /** Every list's name, finished ones too; the suggested name avoids them. */
    takenNames: string[];
    /** From ?list=<id>: open with "Add to an existing list" on this list. */
    preselectedListId?: string | null;
}) {
    const navigate = useNavigate();
    const preselected = lists.find((l) => l.id === preselectedListId);
    const generate = useGeneratePlanShoppingList();
    // isPending lags a render behind the click, so a fast double tap could
    // still get two requests out. The ref closes that gap.
    const inFlight = useRef(false);

    const ranges = useMemo(() => quickRanges(plan), [plan]);
    // "Add days" from a list opens on the days after it, not on days it already has.
    const [initialRange] = useState<DayRange>(
        () => rangeAfterList(plan, preselected?.endDate) ?? defaultRange(plan),
    );
    const [range, setRange] = useState<DayRange>(initialRange);
    const [selected, setSelected] = useState<Set<string>>(() =>
        defaultSelection(
            plan,
            initialRange,
            datesBetween(preselected?.startDate, preselected?.endDate),
        ),
    );
    const [expanded, setExpanded] = useState<Set<number>>(() => new Set([initialRange.from]));
    const [showAll, setShowAll] = useState(false);
    const [destination, setDestination] = useState<'new' | 'existing'>(
        preselected ? 'existing' : 'new',
    );
    const [listId, setListId] = useState(preselected?.id ?? '');
    const [customName, setCustomName] = useState<string | null>(null);
    // Servings (#395). null: each meal starts at its recipe's yield. A number:
    // every meal shops for that many people, one serving each. A meal the
    // client steps up or down afterwards keeps its own number.
    const [cookingFor, setCookingFor] = useState<number | null>(null);
    const [servingOverrides, setServingOverrides] = useState<Map<string, number>>(() => new Map());
    const servingsOf = (meal: PlanMeal) => servingsFor(meal, cookingFor, servingOverrides);

    const chooseCookingFor = (next: number | null) => {
        setCookingFor(next);
        // "Cooking for" sets every meal, including ones changed by hand before.
        setServingOverrides(new Map());
    };

    const stepMealServings = (meal: PlanMeal, direction: 1 | -1) => {
        setServingOverrides((prev) => {
            const next = new Map(prev);
            next.set(meal.key, stepServings(servingsFor(meal, cookingFor, prev), direction));
            return next;
        });
    };

    const targetList = destination === 'existing' ? lists.find((l) => l.id === listId) : undefined;
    const onListDates = useMemo(
        () => datesBetween(targetList?.startDate, targetList?.endDate),
        [targetList?.startDate, targetList?.endDate],
    );

    const selection = useMemo(() => selectedMeals(plan, selected), [plan, selected]);
    const dayCount = selection.dayNumbers.length;
    const mealCount = selection.meals.length;
    const suggestedName = useMemo(
        () => uniqueListName(plan.program.name, selection.dayNumbers, takenNames),
        [plan.program.name, selection.dayNumbers, takenNames],
    );
    const name = customName ?? suggestedName;

    const daysOnList = plan.days.filter(
        (d) => onListDates.has(d.date) && d.dayNumber >= range.from && d.dayNumber <= range.to,
    );
    const tickedDaysOnList = plan.days.filter(
        (d) => onListDates.has(d.date) && selection.dayNumbers.includes(d.dayNumber),
    );
    const addingAgain = tickedDaysOnList.length > 0;

    const visibleCount = showAll ? plan.days.length : Math.max(INITIAL_DAYS, range.to);
    const visibleDays = plan.days.slice(0, visibleCount);

    const withoutListDays = (keys: Set<string>, dates: Set<string>) => {
        const next = new Set(keys);
        for (const day of plan.days) {
            if (!dates.has(day.date)) continue;
            for (const meal of day.meals) next.delete(meal.key);
        }
        return next;
    };

    const applyRange = (next: DayRange) => {
        const from = Math.max(1, Math.min(next.from, plan.duration));
        const to = Math.max(from, Math.min(next.to, plan.duration));
        setRange({ from, to });
        setSelected(defaultSelection(plan, { from, to }, onListDates));
        setExpanded(new Set([from]));
    };

    const chooseList = (id: string) => {
        setListId(id);
        const list = lists.find((l) => l.id === id);
        const dates = datesBetween(list?.startDate, list?.endDate);
        setSelected((prev) => withoutListDays(prev, dates));
    };

    const chooseDestination = (next: 'new' | 'existing') => {
        setDestination(next);
        if (next === 'existing') {
            const id = listId || lists[0]?.id || '';
            if (id) chooseList(id);
        }
    };

    const toggleMeal = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleDay = (day: PlanDay) => {
        const keys = day.meals.filter(isShoppable).map((m) => m.key);
        setSelected((prev) => {
            const next = new Set(prev);
            const allTicked = keys.every((k) => next.has(k));
            for (const k of keys) {
                if (allTicked) next.delete(k);
                else next.add(k);
            }
            return next;
        });
    };

    const toggleExpanded = (dayNumber: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(dayNumber)) next.delete(dayNumber);
            else next.add(dayNumber);
            return next;
        });
    };

    const needsList = destination === 'existing' && !targetList;
    const canSubmit = mealCount > 0 && !needsList && !generate.isPending;

    const submit = async () => {
        if (!canSubmit || inFlight.current) return;
        inFlight.current = true;
        try {
            const payload = buildPayload({
                meals: selection.meals,
                servings: servingsOf,
                destination,
                name,
                shoppingListId: targetList?.id,
                includeMealsAlreadyOnList: addingAgain,
            });
            const result = await generate.mutateAsync(payload);
            const lines = describeResult(result);
            toast.success(
                <div className="space-y-1">
                    {lines.map((line) => (
                        <p key={line}>{line}</p>
                    ))}
                </div>,
                { duration: 8000 },
            );
            // The list page shows the warnings and unlisted meals on the list.
            navigate(shoppingListPath(result.id), { state: routeStateFor(result) });
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string | string[] } } };
            const message = err.response?.data?.message;
            toast.error(
                Array.isArray(message)
                    ? message.join(' ')
                    : message || "We couldn't make your list. Please try again.",
            );
        } finally {
            inFlight.current = false;
        }
    };

    const summary = (
        <>
            <span className="font-semibold text-foreground">
                {dayCount} day{dayCount === 1 ? '' : 's'}, {mealCount} meal
                {mealCount === 1 ? '' : 's'}
            </span>{' '}
            <span className="text-muted-foreground">selected</span>
        </>
    );
    const submitLabel = generate.isPending
        ? destination === 'existing'
            ? 'Adding...'
            : 'Making your list...'
        : destination === 'existing'
          ? 'Add to my list'
          : 'Make my list';

    const statusLine =
        plan.phase === 'UPCOMING'
            ? `starts ${formatShortDate(plan.startDate)}`
            : plan.phase === 'IN_PROGRESS'
              ? `Day ${plan.currentDayNumber} of ${plan.duration}`
              : `finished ${formatShortDate(plan.endDate)}`;

    // No weekday here: two selects side by side on a phone cut "Tue 13 Oct" off.
    const dayOption = (d: PlanDay) =>
        `Day ${d.dayNumber} · ${fromLocalDateStr(d.date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
        })}`;

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 pb-32 sm:px-6 lg:pb-10">
            <Link
                to="/my-menu"
                className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand-text-blue"
            >
                <ArrowLeft className="h-4 w-4" />
                My Menu
            </Link>
            <h1 className="text-3xl font-bold text-foreground">Shop for your plan</h1>
            <p className="mb-6 text-muted-foreground">
                {plan.program.name} · {statusLine}
            </p>

            <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
                <div className="space-y-4">
                    <section className="rounded-2xl bg-card p-4 shadow-sm sm:p-5">
                        <h2 className="mb-3 font-semibold text-foreground">Which days?</h2>
                        <div className="mb-4 flex flex-wrap gap-2">
                            {ranges.map((r) => {
                                const active = r.from === range.from && r.to === range.to;
                                return (
                                    <button
                                        key={r.id}
                                        type="button"
                                        aria-pressed={active}
                                        onClick={() => applyRange(r)}
                                        className={cn(
                                            'inline-flex items-center gap-1 rounded-full border px-4 py-1.5 text-sm transition-colors',
                                            active
                                                ? 'border-brand-header bg-brand-header-wash font-semibold text-brand-text-blue'
                                                : 'border-border bg-background text-foreground hover:bg-brand-header-wash',
                                        )}
                                    >
                                        {active && <Check className="h-4 w-4" />}
                                        {r.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="plan-from" className="mb-1 text-xs">
                                    From
                                </Label>
                                <select
                                    id="plan-from"
                                    value={range.from}
                                    onChange={(e) => {
                                        const from = Number(e.target.value);
                                        applyRange({ from, to: Math.max(from, range.to) });
                                    }}
                                    className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
                                >
                                    {plan.days.map((d) => (
                                        <option key={d.dayNumber} value={d.dayNumber}>
                                            {dayOption(d)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label htmlFor="plan-to" className="mb-1 text-xs">
                                    To
                                </Label>
                                <select
                                    id="plan-to"
                                    value={range.to}
                                    onChange={(e) => {
                                        const to = Number(e.target.value);
                                        applyRange({ from: Math.min(range.from, to), to });
                                    }}
                                    className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
                                >
                                    {plan.days.map((d) => (
                                        <option key={d.dayNumber} value={d.dayNumber}>
                                            {dayOption(d)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </section>

                    <section className="rounded-2xl bg-card p-4 shadow-sm sm:p-5">
                        <h2 className="mb-1 font-semibold text-foreground">Cooking for</h2>
                        <p className="mb-3 text-sm text-muted-foreground">
                            {cookingFor === null
                                ? 'Each meal starts at what its recipe makes. Pick how many people you cook for to shop for one serving each, or change a meal below.'
                                : `Every meal is set to ${describeServings(cookingFor)}, one per person. You can still change a meal below.`}
                        </p>
                        <div className="flex flex-wrap gap-2" role="group" aria-label="Cooking for">
                            <button
                                type="button"
                                aria-pressed={cookingFor === null}
                                onClick={() => chooseCookingFor(null)}
                                className={cn(
                                    'inline-flex h-10 items-center gap-1 rounded-full border px-4 text-sm transition-colors',
                                    cookingFor === null
                                        ? 'border-brand-header bg-brand-header-wash font-semibold text-brand-text-blue'
                                        : 'border-border bg-background text-foreground hover:bg-brand-header-wash',
                                )}
                            >
                                {cookingFor === null && <Check className="h-4 w-4" />}
                                Recipe servings
                            </button>
                            {COOKING_FOR_OPTIONS.map((n) => {
                                const active = cookingFor === n;
                                return (
                                    <button
                                        key={n}
                                        type="button"
                                        aria-pressed={active}
                                        aria-label={`Cooking for ${n}`}
                                        onClick={() => chooseCookingFor(n)}
                                        className={cn(
                                            'inline-flex h-10 min-w-10 items-center justify-center rounded-full border px-3 text-sm transition-colors',
                                            active
                                                ? 'border-brand-header bg-brand-header-wash font-semibold text-brand-text-blue'
                                                : 'border-border bg-background text-foreground hover:bg-brand-header-wash',
                                        )}
                                    >
                                        {n}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <p className="px-1 text-sm text-muted-foreground">
                        Untick any meal you don't need to buy for. Open a day to change its
                        servings.
                    </p>

                    {targetList && daysOnList.length > 0 && (
                        <div className="flex gap-2 rounded-xl border border-border bg-brand-header-wash p-3 text-sm text-foreground">
                            <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-text-blue" />
                            <p>
                                {describeDays(daysOnList.map((d) => d.dayNumber))}{' '}
                                {daysOnList.length === 1 ? 'is' : 'are'} already on{' '}
                                <strong>{targetList.name}</strong>, so{' '}
                                {daysOnList.length === 1 ? 'it starts' : 'they start'} unticked.
                            </p>
                        </div>
                    )}

                    <div className="space-y-2">
                        {visibleDays.map((day) => (
                            <DayRow
                                key={day.dayNumber}
                                day={day}
                                selected={selected}
                                expanded={expanded.has(day.dayNumber)}
                                onToggleExpanded={() => toggleExpanded(day.dayNumber)}
                                onToggleDay={() => toggleDay(day)}
                                onToggleMeal={toggleMeal}
                                servingsOf={servingsOf}
                                onStepServings={stepMealServings}
                                onListName={
                                    onListDates.has(day.date) ? targetList?.name : undefined
                                }
                            />
                        ))}
                    </div>
                    {visibleCount < plan.days.length && (
                        <div className="text-center">
                            <Button
                                variant="link"
                                onClick={() => setShowAll(true)}
                                className="text-brand-text-blue"
                            >
                                Show days {visibleCount + 1} to {plan.days.length}
                            </Button>
                        </div>
                    )}
                </div>

                <aside className="space-y-4 lg:sticky lg:top-4">
                    <section className="rounded-2xl bg-card p-4 shadow-sm sm:p-5">
                        <h2 className="mb-3 font-semibold text-foreground">Where should it go?</h2>
                        <div
                            className="space-y-3"
                            role="radiogroup"
                            aria-label="Where should it go?"
                        >
                            <div
                                className={cn(
                                    'rounded-xl border-2 p-3',
                                    destination === 'new'
                                        ? 'border-brand-header bg-brand-header-wash/50'
                                        : 'border-border',
                                )}
                            >
                                <label className="flex cursor-pointer items-center gap-2 font-semibold">
                                    <input
                                        type="radio"
                                        name="destination"
                                        value="new"
                                        checked={destination === 'new'}
                                        onChange={() => chooseDestination('new')}
                                        className="h-4 w-4 accent-[var(--brand-header)]"
                                    />
                                    New list
                                </label>
                                {destination === 'new' && (
                                    <div className="mt-3">
                                        <Label htmlFor="plan-list-name" className="mb-1 text-sm">
                                            List name
                                        </Label>
                                        <Input
                                            id="plan-list-name"
                                            value={name}
                                            onChange={(e) => setCustomName(e.target.value)}
                                            maxLength={120}
                                        />
                                    </div>
                                )}
                            </div>

                            <div
                                className={cn(
                                    'rounded-xl border-2 p-3',
                                    destination === 'existing'
                                        ? 'border-brand-header bg-brand-header-wash/50'
                                        : 'border-border',
                                    lists.length === 0 && 'opacity-70',
                                )}
                            >
                                <label
                                    className={cn(
                                        'flex items-center gap-2 font-semibold',
                                        lists.length > 0 ? 'cursor-pointer' : 'cursor-not-allowed',
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name="destination"
                                        value="existing"
                                        checked={destination === 'existing'}
                                        onChange={() => chooseDestination('existing')}
                                        disabled={lists.length === 0}
                                        className="h-4 w-4 accent-[var(--brand-header)]"
                                    />
                                    Add to an existing list
                                </label>
                                {lists.length === 0 && (
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        You don't have any open lists yet.
                                    </p>
                                )}
                                {destination === 'existing' && lists.length > 0 && (
                                    <div className="mt-3">
                                        <Label htmlFor="plan-list" className="sr-only">
                                            List
                                        </Label>
                                        <select
                                            id="plan-list"
                                            value={listId}
                                            onChange={(e) => chooseList(e.target.value)}
                                            className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm"
                                        >
                                            {lists.map((l) => (
                                                <option key={l.id} value={l.id}>
                                                    {l.name}
                                                    {l.startDate && l.endDate
                                                        ? ` (${formatShortDate(l.startDate.slice(0, 10))} to ${formatShortDate(l.endDate.slice(0, 10))})`
                                                        : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="mt-2 text-xs text-muted-foreground">
                                            We only add. Your ticks and your own items stay as they
                                            are.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {addingAgain && targetList && (
                            <div
                                role="alert"
                                className="mt-4 flex gap-2 rounded-xl border border-brand-fill-yellow bg-brand-fill-yellow/25 p-3 text-sm text-foreground"
                            >
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <p>
                                    <strong>
                                        {describeDays(tickedDaysOnList.map((d) => d.dayNumber))}{' '}
                                        {tickedDaysOnList.length === 1 ? 'is' : 'are'} already on{' '}
                                        {targetList.name}.
                                    </strong>{' '}
                                    Adding {tickedDaysOnList.length === 1 ? 'it' : 'them'} again
                                    doubles those amounts.
                                </p>
                            </div>
                        )}

                        <div className="mt-4 hidden border-t border-border pt-4 text-center lg:block">
                            <p className="mb-3 text-sm">{summary}</p>
                            <Button
                                size="lg"
                                className="h-12 w-full text-base"
                                onClick={submit}
                                disabled={!canSubmit}
                            >
                                <ShoppingCart className="h-5 w-5" />
                                {submitLabel}
                            </Button>
                        </div>
                    </section>
                </aside>
            </div>

            {/* Phone: the count and the button stay in reach while scrolling the days. */}
            <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] lg:hidden">
                <div className="mx-auto flex max-w-6xl items-center gap-3">
                    <p className="min-w-0 flex-1 text-sm leading-tight">{summary}</p>
                    <Button
                        size="lg"
                        className="h-12 shrink-0 px-5 text-base"
                        onClick={submit}
                        disabled={!canSubmit}
                    >
                        <ShoppingCart className="h-5 w-5" />
                        {submitLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
}

const ShopForPlanContent = () => {
    const [searchParams] = useSearchParams();
    const { data: plan, isLoading, isError } = useMyPlan();
    const { data: lists = [], isLoading: listsLoading } = useShoppingListSummaries(true);
    const { data: allNames, isLoading: namesLoading } = useShoppingListNames(true);
    const takenNames = useMemo(
        () => [...(allNames ?? []), ...lists.map((list) => list.name)],
        [allNames, lists],
    );

    if (isLoading || listsLoading || namesLoading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                    <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-brand-header" />
                    <p className="text-muted-foreground">Loading your plan...</p>
                </div>
            </div>
        );
    }

    if (isError || !plan) {
        return (
            <div className="mx-auto max-w-xl px-4 py-10">
                <div className="rounded-3xl bg-card p-8 text-center shadow-sm">
                    <UtensilsCrossed className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                    <h1 className="mb-2 text-xl font-bold text-foreground">
                        {isError ? "We couldn't load your plan" : 'No plan yet'}
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        {isError
                            ? 'Please check your connection and try again.'
                            : 'Once your coach assigns a plan, you can shop for it here.'}
                    </p>
                    <Button asChild variant="outline">
                        <Link to="/shopping-lists">Go to your shopping lists</Link>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Picker
            plan={plan}
            lists={lists as ListWithRange[]}
            takenNames={takenNames}
            preselectedListId={searchParams.get('list')}
        />
    );
};

const ShopForPlan = () => (
    <FeatureGate feature={Feature.SHOPPING_LISTS} fallback={<ShoppingListsLockedPage />}>
        <ShopForPlanContent />
    </FeatureGate>
);

export default ShopForPlan;
